import { Dispatch, SetStateAction, useEffect } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Prompt } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { ContainedButton } from 'components/Button';
import { hasKeptChanges, ReportedWorkRow } from './tableUtils';
import { FullReport, FullReportDay } from './types';

interface FullReportSaveControlsProps {
  control: Control<FullReport>;
  // react-hook-form's dirty fields; updated in place as fields change.
  dirtyFields: { days?: unknown };
  hoursOnly: boolean;
  getSavedDay: (dayId?: string) => ReportedWorkRow | undefined;
  isBusy: boolean;
  onSubmit: () => void;
  setIsFormedChanged: Dispatch<SetStateAction<boolean>>;
}

// Save button and leave prompt of the full report. Edits the save would not
// keep (a client or project picked on a row without hours or times) leave the
// report unchanged: they neither enable Save nor ask before leaving; the
// selection stays on screen until the report reloads. Whether an edit is kept
// depends on the values as typed (blank hours, then 8), so this watches the
// days itself: typing re-renders these controls, not the whole table.
const FullReportSaveControls = ({
  control,
  dirtyFields,
  hoursOnly,
  getSavedDay,
  isBusy,
  onSubmit,
  setIsFormedChanged,
}: FullReportSaveControlsProps) => {
  const intl = useIntl();
  const days = (useWatch({ control, name: 'days' }) || []) as Array<
    FullReportDay | undefined
  >;
  const hasChanges = hasKeptChanges(
    dirtyFields.days as Array<Record<string, unknown> | undefined> | undefined,
    days,
    days.map((day) => getSavedDay(day?.dayId)),
    hoursOnly,
  );

  useEffect(() => {
    setIsFormedChanged(hasChanges);
  }, [hasChanges, setIsFormedChanged]);

  return (
    <>
      <Prompt
        when={hasChanges}
        message={intl.formatMessage({ id: 'PROMPT.LEAVE' })}
      />
      <Box display="flex" justifyContent="flex-end" marginTop="40px">
        <ContainedButton
          onClick={onSubmit}
          size="large"
          disabled={!hasChanges || isBusy}
        >
          {intl.formatMessage({
            id: `MODAL.BUTTON.SUBMIT`,
          })}
        </ContainedButton>
      </Box>
    </>
  );
};

export default FullReportSaveControls;
